import { Key, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { Table, Text } from '@mantine/core'
import { pikStatuses, frequencyOptions } from 'app/models/dropdown-options'
import { setOneFacility } from 'app/state/ducks/facilities/thunks'
import { getFacilityAmendments } from 'app/state/ducks/facility-amendments/selectors'
import { getLoanAmounts } from 'app/state/ducks/loans/selectors'
import { calculatePikScheduler, exportToCSV, formatDateToShow, stringToDate } from 'app/utils/util-functions'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { NewSelectInput } from '../inputs/selects/NewSelectInput'
import ControlSection from 'app/views/pages/accounts-management/entities/components/control-section'
import { LoanParams } from 'app/models/loan-params'
import { DatePickerInput, DatePickerInputProps } from '@mantine/dates'
import IonIcon from '@reacticons/ionicons'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'
import 'app/views/components/interest-payment-schedule/interest-payment-schedule-details.css'

interface Props {
  facilityId?: string
  loan: LoanParams
  tab: string
}

type PikSchedulerData = {
  pikDate: string,
  status: string
}

// Extend the props type to include placeholder, w, and h
interface CustomDatePickerInputProps extends DatePickerInputProps<"range"> {
  placeholder?: string;
}

export default function PIKSchedulerDetails({
  facilityId,
  loan,
  tab
}: Props) {
  const dispatch = useDispatch()
  const facilityAmendments = useSelector(getFacilityAmendments)
  const facility = useSelector(getFacility)
  const loanAmounts = useSelector(getLoanAmounts)
  const businessDate = useSelector(getBusinessDate)
  const [isLoading, setIsLoading] = useState(true)
  const [currentRows, setCurrentRows] = useState<any>([])
  const [historyRows, setHistoryRows] = useState<any>([])
  const [pikScheduler, setPikScheduler] = useState<PikSchedulerData[]>([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedStatus, setSelectedStatus] = useState('All')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const startIdx = (activePage - 1) * itemsPerPage
  const endIdx = startIdx + itemsPerPage

  useEffect(() => {
    if (facilityAmendments && facilityAmendments.length > 0) {
      const amendment = facilityAmendments.find(
        (amendment: any) => amendment.id === facilityId
      )
      if (amendment) {
        dispatch(setOneFacility(amendment))
      }
    }
  }, [facilityAmendments, facilityId])

  useEffect(() => {
    setIsLoading(true)
    const pikScheduler = calculatePikScheduler(
      facility?.pikScheduler?.pikFrequency,
      stringToDate(loan?.nextPIKDate ?? ''),
      stringToDate(loan?.endDate ?? ''),
      businessDate
    );
    setPikScheduler(pikScheduler)
    setIsLoading(false)
  }, [loan]);

  useEffect(() => {
    setIsLoading(true)
    // Filter by status and date range
    const filteredPikPayment = pikScheduler?.filter((scheduler: PikSchedulerData) => {
      const pikDate = new Date(scheduler.pikDate);
      const [startDate, endDate] = dateRange;

      // Status filter
      const statusMatch =
        selectedStatus === 'All' ||
        pikStatuses.find(({ label }) => label === selectedStatus)?.value === scheduler.status;

      // Date range filter (if both dates are set, filter within range; otherwise, include all)
      const dateMatch =
        (!startDate || pikDate >= startDate) && (!endDate || pikDate <= endDate);

      return statusMatch && dateMatch;
    });

    const filteredCurrentRows = filteredPikPayment
      ?.filter((scheduler: PikSchedulerData) => scheduler.status !== 'Paid')
      .slice(startIdx, endIdx)
      .map((scheduler: PikSchedulerData, index: Key | null | undefined) => {
        return (
          <tr className='interest-payment-table-row' key={index}>
            <td className='interest-payment-table-body-cell column-1'><Text className='interest-payment-table-body-cell-text'>{formatDateToShow(new Date(scheduler?.pikDate)).toUpperCase()}</Text></td>
            <td className='interest-payment-table-body-cell column-2'>
              <Text
                className='interest-payment-table-body-cell-text'
                style={{
                  color:
                    scheduler?.status === 'Past_Due'
                      ? '#EE5959'
                      : scheduler?.status === 'Scheduled'
                        ? '#287A26'
                        : 'inherit',
                }}
              >
                {pikStatuses.find(({ value }) => value === scheduler.status)?.label}
              </Text>
            </td>
          </tr>
        )
      })
    setCurrentRows(filteredCurrentRows)

    // History tab rows (filter loanAmounts by date range)
    const filteredHistoryRows = Array.isArray(loanAmounts)
      ? loanAmounts
        .filter((history) => {
          const effectiveDate = history.effectiveDate ? stringToDate(history.effectiveDate) : null;
          const [startDate, endDate] = dateRange;
          const dateMatch =
            (!startDate || (effectiveDate && effectiveDate >= startDate)) &&
            (!endDate || (effectiveDate && effectiveDate <= endDate));
          return (
            (history.actionType === 'PIK' || history.actionType === 'RolloverWithPik') && dateMatch
          );
        })
        .map((history: any, index: Key | null | undefined) => (
          <tr className="interest-payment-table-row" key={index}>
            <td className="interest-payment-table-body-cell column-full-width">
              <Text className="interest-payment-table-body-cell-text">
                {formatDateToShow(history?.effectiveDate ? stringToDate(history.effectiveDate) : undefined)}
              </Text>
            </td>
          </tr>
        )) : [];
    setHistoryRows(filteredHistoryRows);
    setIsLoading(false)
  }, [pikScheduler, selectedStatus, dateRange, activePage, itemsPerPage])



  useEffect(() => {
    setActivePage(1);
    setCurrentRows([]); // Reset to force recalculation
    setHistoryRows([]); // Reset to force recalculation
  }, [tab]);

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value, 10))
    setActivePage(1)
  }

  const statusOptions = ['All', 'Past Due', 'Scheduled']
  const facilityFrequency = facility?.iroValues?.find((iroValues: { indexOption: { id: string } }) => iroValues.indexOption?.id === loan?.indexOption?.id)?.interestPaymentFrequency
  const facilityFirstPaymentDate = facility?.iroValues?.find((iroValues: { indexOption: { id: string } }) => iroValues.indexOption?.id === loan?.indexOption?.id)?.interestFirstPaymentDate

  const DatePickerInputWithPlaceholder = DatePickerInput as React.ComponentType<CustomDatePickerInputProps>

  return (
    <div className='schedule-outer-box'>
      <div className='schedule-container'>
        <div className='schedule-inner-box'>
          <div className='schedule-header'>
            <Text className='schedule-header-text'>PIK Schedule</Text>
            <div className='schedule-header-group'>
              <div className='frequency-group'>
                <div className='frequency-label-wrapper'>
                  <Text className='frequency-label'>
                    Frequency
                  </Text>
                </div>
                <div className='interest-payment-frequency-group'>
                  <div className='interest-payment-frequency'>
                    {frequencyOptions.find(
                      option => option.value === facilityFrequency
                    )?.label ?? 'N/A'}
                  </div>
                </div>
              </div>
              {tab === 'history' && (
                <div className='frequency-group'>
                  <div className='frequency-label-wrapper'>
                    <Text className='frequency-label'>
                      First PIK Date
                    </Text>
                  </div>
                  <div className='interest-payment-frequency-group'>
                    <div className='interest-payment-frequency'>
                      {facilityFirstPaymentDate ? formatDateToShow(facilityFirstPaymentDate as Date).toUpperCase() : 'N/A'}
                    </div>
                  </div>
                </div>
              )}
              {tab !== 'history' && (
                <div className='first-payment-date-group'>
                  <div className='first-payment-date-label-wrapper'><Text className='first-payment-date-label'>Date Range</Text></div>
                  <div>
                    <DatePickerInputWithPlaceholder
                      w="281px"
                      h="42px"
                      placeholder='Select Date Range'
                      valueFormat='DD-MMM-YYYY'
                      type="range"
                      firstDayOfWeek={0}
                      classNames={{
                        input: 'newInput',
                        wrapper: 'newInputWrapper',
                        rightSection: 'customRightSection',
                      }}
                      rightSection={<IonIcon size="large" name="chevron-down-outline"></IonIcon>}
                      value={dateRange}
                      onChange={setDateRange}
                      sx={{ '& .mantine-DatePickerInput-input': { textAlign: 'center' } }}
                    />
                  </div>

                  <div className='status-wrapper'>
                    <NewSelectInput
                      label='Status'
                      id='status-select'
                      className='status-select'
                      data={statusOptions}
                      onChange={(value) => {
                        setSelectedStatus(value ?? 'All')
                        setActivePage(1)
                      }}
                      defaultValue='All' />
                  </div>

                </div>
              )}
            </div>
          </div>
          {tab === 'history' && (<div className='schedule-subheader'><Text className='schedule-subheader-text'>PIK Date</Text></div>)}
          <div className='interest-payment-table-wrapper'>
            {isLoading ? (
              <CustomLoaderPage />
            ) : (
              <Table className='interest-payment-table' highlightOnHover fontSize="xl" w='100%'>
                <thead>
                  <tr className='interest-payment-table-row'>
                    <th className={`interest-payment-table-header-cell ${tab === 'current' ? 'column-1' : 'column-full-width'}`}><Text className='interest-payment-table-header-text'>Due Date</Text></th>
                    {tab !== 'history' && (
                      <th className='interest-payment-table-header-cell column-2'><Text className='interest-payment-table-header-text'>Status</Text></th>
                    )}
                  </tr>
                </thead>
                <tbody className='interest-payment-table-body'>{tab === 'current' ? currentRows : historyRows}</tbody>
              </Table>
            )}
          </div>
          <div className='interest-payment-table-control-section'>
            <ControlSection
              itemsPerPage={itemsPerPage}
              activePage={activePage}
              totalItems={Math.max(1, pikScheduler.filter(
                (scheduler: PikSchedulerData) =>
                  selectedStatus === 'All' ||
                  pikStatuses.find(({ label }) => label === selectedStatus)?.value === scheduler.status
              ).filter((scheduler: PikSchedulerData) => (tab === 'history' ? scheduler.status === 'Paid' : scheduler.status !== 'Paid')).length)}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={setActivePage}
              onExport={() => exportToCSV(pikScheduler, 'pik-payment-schedule.csv')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}